import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { GLUEX_EXCHANGE_RATES_URL } from '@/config/consts'
import { SuspenseQueryWith } from '@/utils/types'
import { hyperEVM } from '@/config/chain/constants'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TokenWithBalance } from '@/domain/common/types'

interface APIResponse {
  tokens: {
    [chainId: string]: {
      chainId: number
      address: CheckedAddress
      symbol: TokenSymbol
      decimals: number
      name: string
      logoURI?: string
    }[]
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getSwapTokens() {
  return queryOptions<TokenWithBalance[]>({
    queryKey: ['swap-tokens'],
    queryFn: async () => {
      try {
        const res = await fetch(`${GLUEX_EXCHANGE_RATES_URL}/tokens?chains=${hyperEVM.id}`)
        if (!res.ok) {
          throw new Error('Failed to fetch tokens')
        }
        const data = (await res.json()) as APIResponse
        const hyperEVMTokens = data.tokens[hyperEVM.id]

        if (!hyperEVMTokens) {
          throw new Error('No tokens found for hyperEVM')
        }

        const formattedTokens: TokenWithBalance[] = hyperEVMTokens.map((token) => ({
          token: new Token({
            address: token.address,
            decimals: token.decimals,
            name: token.name,
            symbol: token.symbol,
            unitPriceUsd: '0',
          }),
          balance: NormalizedUnitNumber(0),
        }))
        return formattedTokens
      } catch (error) {
        console.error(error)
        return []
      }
    },
  })
}

export type UseGetSwapTokensResult = SuspenseQueryWith<{ tokens: TokenWithBalance[] }>

export function useGetSwapTokens(): UseGetSwapTokensResult {
  const response = useSuspenseQuery({
    ...getSwapTokens(),
  })

  return {
    ...response,
    tokens: response.data,
  }
}
